import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Anchor,
    Button,
    Select,
    Stack,
    Text,
    TextInput,
    Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconExclamationCircle, IconLayoutGrid } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import useSWR, { useSWRConfig } from 'swr';
import {
    createScene,
    getScene,
    listComponents,
    panelSizeLabel,
    SCENE_TEMPLATES,
    updateScene,
    type PropEntry,
    type Scene,
    type SceneTemplateId,
    type ShellComponent,
} from '../../data/ShellConfig';
import { useCsrf } from '../../hooks/useCsrf';
import { track } from '../../utils/mixpanel';
import { AnalyticsEvents } from '../../analytics/events';
import PropEditor from './PropEditor';
import SceneTemplatePicker from './SceneTemplatePicker';
import { useRouterDrawerCloseOptional } from './RouterDrawer';

type PanelDraft = {
    instanceId?: string;
    componentId: string | null;
    propOverrides: PropEntry[];
};

type SceneFormProps = {
    sceneId?: string;
    onSaved?: (scene: Scene) => void;
    /** Called after save when not inside a RouterDrawer. */
    onClose?: () => void;
    /** Show a link to open/create this scene in the full Studio editor. */
    showStudioLink?: boolean;
};

function isSceneTemplateId(value: string): value is SceneTemplateId {
    return SCENE_TEMPLATES.some(t => t.id === value);
}

function panelsFromScene(scene: Scene): PanelDraft[] {
    const sorted = [...(scene.panels ?? [])].sort((a, b) => a.index - b.index);
    return sorted.map(p => ({
        instanceId: p.id,
        componentId: p.componentId,
        propOverrides: p.propOverrides ?? [],
    }));
}

const SceneFormFields: React.FC<{
    initial: Scene | null;
    sceneId?: string;
    onSaved?: (scene: Scene) => void;
    onClose?: () => void;
    showStudioLink?: boolean;
}> = ({ initial, sceneId, onSaved, onClose, showStudioLink = false }) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();
    const closeDrawer = useRouterDrawerCloseOptional();
    const { data: components = [] } = useSWR('api/components', listComponents);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const initialTemplate: SceneTemplateId =
        initial && isSceneTemplateId(initial.templateId) ? initial.templateId : 'full';
    const [templateId, setTemplateId] = useState<SceneTemplateId>(initialTemplate);
    const [panels, setPanels] = useState<PanelDraft[]>(
        initial ? panelsFromScene(initial) : [{ componentId: null, propOverrides: [] }]
    );
    const skipNextTemplateSync = useRef(Boolean(initial));
    const isEdit = Boolean(sceneId);

    const form = useForm({
        initialValues: { name: initial?.name ?? '' },
        validate: {
            name: v => (v.trim() ? null : 'Name is required'),
        },
    });

    useEffect(() => {
        if (skipNextTemplateSync.current) {
            skipNextTemplateSync.current = false;
            return;
        }
        const template = SCENE_TEMPLATES.find(t => t.id === templateId)!;
        setPanels(prev =>
            template.panels.map((_, i) => prev[i] ?? { componentId: null, propOverrides: [] })
        );
    }, [templateId]);

    const componentOptions = components.map((c: ShellComponent) => ({
        value: c.id,
        label: c.name,
    }));

    const handleSubmit = async (values: typeof form.values) => {
        if (panels.some(p => !p.componentId)) {
            setError('Select a Component for every panel');
            return;
        }
        setSaving(true);
        setError('');
        const payload = {
            name: values.name.trim(),
            templateId,
            panels: panels.map(p => ({
                instanceId: p.instanceId,
                componentId: p.componentId!,
                propOverrides: p.propOverrides,
            })),
        };
        try {
            const saved = isEdit
                ? await updateScene(sceneId!, payload, csrfToken)
                : await createScene(payload, csrfToken);
            track(isEdit ? AnalyticsEvents.SceneUpdated : AnalyticsEvents.SceneCreated, {
                source: 'scene_form',
                sceneId: saved.id,
                templateId,
                panelCount: payload.panels.length,
            });
            // Seed the detail cache with the freshly saved scene so that
            // re-opening this form (via useSWR on `api/scenes/${id}`)
            // doesn't briefly show the stale pre-save snapshot before the
            // background revalidation catches up.
            await mutate(`api/scenes/${saved.id}`, saved, { revalidate: false });
            onSaved?.(saved);
            if (closeDrawer) {
                closeDrawer();
            } else {
                onClose?.();
            }
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Failed to save scene');
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
                {(isEdit || showStudioLink) && (
                    <Anchor
                        component={Link}
                        to={`/studio/scenes/${isEdit ? sceneId : 'new'}`}
                        onClick={() => onClose?.()}
                        size="sm"
                        underline="hover"
                    >
                        <Group gap={4} wrap="nowrap" c="violet">
                            <IconLayoutGrid size={14} />
                            {isEdit ? 'Open in Studio' : 'Create in the Studio'}
                        </Group>
                    </Anchor>
                )}

                <TextInput
                    withAsterisk
                    label="Scene Name"
                    placeholder="e.g. Live game layout"
                    {...form.getInputProps('name')}
                />

                <SceneTemplatePicker templateId={templateId} onChange={setTemplateId} />

                {panels.map((panel, index) => {
                    const height = SCENE_TEMPLATES.find(t => t.id === templateId)!.panels[index];
                    const selected = components.find(c => c.id === panel.componentId);
                    return (
                        <Stack
                            key={panel.instanceId ?? index}
                            gap="xs"
                            p="sm"
                            style={{
                                border: '1px solid var(--mantine-color-gray-4)',
                                borderRadius: 8,
                            }}
                        >
                            <Text size="sm" fw={600}>
                                Panel {index + 1} ({height}% · {panelSizeLabel(height)} px)
                            </Text>
                            <Select
                                label="Component"
                                placeholder="Select a component"
                                data={componentOptions}
                                value={panel.componentId}
                                onChange={value => {
                                    const next = [...panels];
                                    next[index] = {
                                        ...next[index],
                                        componentId: value,
                                        // Keep existing overrides when only swapping component in edit;
                                        // clear when user picks a different component from empty.
                                        propOverrides:
                                            value === panel.componentId
                                                ? panel.propOverrides
                                                : panel.componentId
                                                  ? []
                                                  : panel.propOverrides,
                                    };
                                    setPanels(next);
                                }}
                                searchable
                            />
                            {selected && (
                                <>
                                    <Text size="xs" c="dimmed">
                                        Override props for this panel only (does not change the Component
                                        definition). Defaults:{' '}
                                        {(selected.props ?? [])
                                            .map(p => `${p.key}=${p.value}`)
                                            .join(', ') || 'none'}
                                    </Text>
                                    <PropEditor
                                        props={panel.propOverrides}
                                        onChange={propOverrides => {
                                            const next = [...panels];
                                            next[index] = { ...next[index], propOverrides };
                                            setPanels(next);
                                        }}
                                    />
                                </>
                            )}
                        </Stack>
                    );
                })}

                {error && (
                    <Alert color="red" icon={<IconExclamationCircle />} title="Error">
                        {error}
                    </Alert>
                )}

                <Button type="submit" loading={saving} disabled={!form.isValid()}>
                    {isEdit ? 'Update Scene' : 'Save Scene'}
                </Button>
            </Stack>
        </form>
    );
};

const SceneForm: React.FC<SceneFormProps> = ({ sceneId, onSaved, onClose, showStudioLink }) => {
    const { data, error, isLoading } = useSWR(sceneId ? `api/scenes/${sceneId}` : null, () =>
        getScene(sceneId!)
    );

    if (sceneId && isLoading) {
        return <Text c="dimmed">Loading…</Text>;
    }
    if (sceneId && (error || !data)) {
        return (
            <Alert color="red" icon={<IconExclamationCircle />} title="Error">
                Could not load scene.
            </Alert>
        );
    }

    return (
        <SceneFormFields
            key={sceneId ?? 'new'}
            initial={data ?? null}
            sceneId={sceneId}
            onSaved={onSaved}
            onClose={onClose}
            showStudioLink={showStudioLink}
        />
    );
};

export default SceneForm;
